import { useQueryClient } from '@tanstack/react-query';
import EventCreative from '../../../features/events/EventCreative';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import { objectStorageApi } from '../../../api/objectStorage';
import HomeEventVisibilityControl from './HomeEventVisibilityControl';
import useAdminDraftGuard from '../useAdminDraftGuard';
import { useUserStore } from '../../../stores/userStore';

const emptyForm = () => ({
  title: '',
  titleEn: '',
  description: '',
  descriptionEn: '',
  imageUrl: '',
  actionLabel: '',
  actionLabelEn: '',
  destinationUrl: '',
  isPinned: false,
  showAdMark: true,
  isActive: true,
});

function EventSession() {
  const queryClient = useQueryClient();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [notice, setNotice] = useState('');
  const [homeBusy, setHomeBusy] = useState(false);
  const pending = useRef(false);
  const life = useRef({ live: true, read: 0 });
  useEffect(() => {
    const session = life.current;
    session.live = true;
    return () => {
      session.live = false;
      session.read += 1;
    };
  }, []);
  const locked = busy || homeBusy;
  const draft = useAdminDraftGuard(form ? { form, file } : null, {
    enabled: form !== null || busy,
    busy,
  });
  const closeForm = () => {
    if (pending.current || !draft.confirmDiscard()) return;
    setForm(null);
    setFile(null);
  };
  const reload = useCallback(async () => {
    const session = life.current;
    const request = ++session.read;
    const current = () => session.live && session.read === request;
    setListError('');
    setLoading(true);
    try {
      const response = await api.getAuth('/admin/ads/events');
      if (current()) setItems(response.data.data ?? []);
    } catch {
      if (current()) setListError('이벤트 목록을 불러오지 못했습니다.');
    } finally {
      if (current()) setLoading(false);
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  useEffect(() => {
    if (!file) {
      setPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const edit = (item) => {
    if (pending.current || homeBusy || !draft.confirmDiscard()) return;
    draft.resetBaseline();
    const defaults = emptyForm();
    setForm(
      item
        ? {
            ...Object.fromEntries(
              Object.entries(defaults).map(([key, value]) => [key, item[key] ?? value]),
            ),
            id: item.id,
          }
        : defaults,
    );
    setFile(null);
    setError('');
  };
  const field = (name) => ({
    value: form[name] || '',
    onChange: (event) => {
      if (!pending.current) setForm({ ...form, [name]: event.target.value });
    },
  });
  const remove = async () => {
    if (pending.current || homeBusy || !deleteTarget) return;
    pending.current = true;
    const target = deleteTarget;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.deleteAuth(`/admin/ads/events/${target.id}`);
      if (!life.current.live) return;
      setItems((current) => current.filter((item) => item.id !== target.id));
      setNotice('이벤트를 삭제했습니다.');
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['public-events'] });
      await reload();
    } catch (failure) {
      if (life.current.live)
        setError(failure.response?.data?.msg || '이벤트를 삭제하지 못했습니다. 다시 시도해주세요.');
    } finally {
      pending.current = false;
      if (life.current.live) setBusy(false);
    }
  };
  const save = async (event) => {
    event.preventDefault();
    if (pending.current || homeBusy || !form) return;
    setError('');
    // Validate text/URL before creating any remote image.
    const payload = { ...form };
    const id = payload.id;
    delete payload.id;
    try {
      if (!payload.actionLabel.trim()) throw new Error('주황색 버튼 문구를 입력해주세요.');
      let url;
      try {
        url = new URL(payload.destinationUrl);
      } catch {
        throw new Error('응모 URL은 인증 정보 없는 HTTPS 주소여야 합니다.');
      }
      if (url.protocol !== 'https:' || url.username || url.password)
        throw new Error('응모 URL은 인증 정보 없는 HTTPS 주소여야 합니다.');
      if (!file && !payload.imageUrl) throw new Error('대표 이미지를 업로드해주세요.');
    } catch (failure) {
      setError(failure.message);
      return;
    }
    pending.current = true;
    setBusy(true);
    setNotice('');
    try {
      if (file) {
        const body = new FormData();
        body.append('image', file);
        const result = await objectStorageApi.uploadImage(body);
        if (!life.current.live) return;
        if (!result.data?.url) throw new Error('이미지 업로드 결과가 없습니다.');
        payload.imageUrl = result.data.url;
        setForm((current) => ({ ...current, imageUrl: payload.imageUrl }));
        setFile(null);
      }
      const response = id
        ? await api.putAuth(`/admin/ads/events/${id}`, payload)
        : await api.postAuth('/admin/ads/events', payload);
      if (!life.current.live) return;
      const saved = response?.data?.data;
      setItems((current) =>
        id
          ? current.map((item) => (item.id === id ? { ...item, ...payload } : item))
          : saved?.id
            ? [{ ...saved, ...payload }, ...current]
            : current,
      );
      draft.markClean();
      setForm(null);
      setFile(null);
      setNotice('이벤트를 저장했습니다.');
      void queryClient.invalidateQueries({ queryKey: ['public-events'] });
      await reload();
    } catch (failure) {
      if (life.current.live)
        setError(failure.response?.data?.msg || failure.message || '저장하지 못했습니다.');
    } finally {
      pending.current = false;
      if (life.current.live) setBusy(false);
    }
  };
  return (
    <Stack spacing={2}>
      <Typography variant="h6">이벤트 관리</Typography>
      <HomeEventVisibilityControl
        disabled={busy || !!form || !!deleteTarget}
        onBusyChange={setHomeBusy}
      />
      <Typography>
        자체 이벤트와 광고 이벤트를 등록합니다. 고정된 항목이 먼저, 같은 그룹에서는 최신 등록순으로
        노출됩니다.
      </Typography>
      {!form && !deleteTarget && error && <Alert severity="error">{error}</Alert>}
      {!form && !deleteTarget && notice && <Alert severity="success">{notice}</Alert>}
      {!form && !deleteTarget && listError && (
        <Alert
          severity="error"
          sx={{
            flexWrap: 'wrap',
            '& .MuiAlert-message': { minWidth: 0, flex: '1 1 120px', overflowWrap: 'anywhere' },
            '& .MuiAlert-action': { ml: 'auto', pl: 0 },
          }}
          action={
            <Button disabled={locked || loading} onClick={reload}>
              재시도
            </Button>
          }
        >
          {notice ? '변경은 완료됐지만 목록을 새로 불러오지 못했습니다. ' : ''}
          {listError}
        </Alert>
      )}
      <Button variant="contained" disabled={locked} onClick={() => edit(null)}>
        이벤트 등록
      </Button>
      {loading ? (
        <Typography>불러오는 중…</Typography>
      ) : (
        !listError && !items.length && <Typography>등록된 이벤트가 없습니다.</Typography>
      )}
      {items.map((item) => (
        <Stack
          key={item.id}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Box
            component="img"
            src={item.imageUrl}
            alt=""
            sx={{ width: 112, aspectRatio: '14 / 9', objectFit: 'cover', borderRadius: 1 }}
          />
          <Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            <Typography>{item.title || item.actionLabel}</Typography>
            <Typography variant="caption">
              {item.isActive ? '공개' : '비공개'} · {item.isPinned ? '상단 고정' : '일반'} ·{' '}
              {item.showAdMark ? 'AD 표시' : 'AD 미표기'}
            </Typography>
          </Box>
          <Button disabled={locked || loading} onClick={() => edit(item)}>
            수정
          </Button>
          <Button
            color="error"
            disabled={locked || loading}
            onClick={() => {
              setError('');
              setDeleteTarget(item);
            }}
          >
            삭제
          </Button>
        </Stack>
      ))}
      <Dialog
        sx={{
          '& .MuiDialog-paper': { m: 1 },
          '& .MuiDialogContent-root': { overflowWrap: 'anywhere', px: 2 },
          '& .MuiDialogActions-root': { flexWrap: 'wrap' },
        }}
        open={!!deleteTarget}
        onClose={() => {
          if (!pending.current) setDeleteTarget(null);
        }}
        aria-labelledby="event-delete-title"
      >
        <DialogTitle id="event-delete-title">이벤트 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            ‘{deleteTarget?.title || deleteTarget?.actionLabel}’ 이벤트를 삭제할까요?
          </Typography>
          <Typography>
            이벤트 목록과 홈 노출에서 제거되며 되돌릴 수 없습니다. 업로드된 이미지 파일은 삭제하지
            않습니다.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setDeleteTarget(null)}>
            취소
          </Button>
          <Button color="error" disabled={busy} onClick={remove}>
            {busy ? '삭제 중…' : '삭제 확인'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!form}
        fullWidth
        maxWidth="sm"
        onClose={closeForm}
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 4 },
            width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
          },
          '& .MuiDialogContent-root': { px: { xs: 1.5, sm: 3 } },
        }}
      >
        {form && (
          <Box component="form" aria-label="이벤트 편집" onSubmit={save}>
            <DialogTitle>{form.id ? '이벤트 수정' : '이벤트 등록'}</DialogTitle>
            <DialogContent>
              <Stack
                component="fieldset"
                disabled={busy}
                spacing={2}
                sx={{ border: 0, p: 0, m: 0, pt: 1, minWidth: 0 }}
              >
                {error && <Alert severity="error">{error}</Alert>}
                <Button component="label" variant="outlined" disabled={busy}>
                  대표 이미지 업로드
                  <input
                    hidden
                    type="file"
                    aria-label="대표 이미지 파일"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event) => {
                      if (pending.current) return;
                      const next = event.target.files?.[0];
                      event.target.value = '';
                      if (!next) return;
                      if (
                        !['image/png', 'image/jpeg', 'image/webp'].includes(next.type) ||
                        next.size > 5 * 1024 * 1024
                      ) {
                        setError('5MB 이하 PNG/JPG/WebP 이미지를 선택하세요.');
                        return;
                      }
                      setFile(next);
                      setError('');
                    }}
                  />
                </Button>
                <Typography variant="caption">
                  이미지와 카드 전체 14:9 · 권장 1400×900px · 하단 44pt 버튼이 이미지 위에 겹칩니다.
                  중요한 이미지는 하단을 피하고, 제목/설명은 이미지에 넣지 않아도 됩니다.
                </Typography>
                <TextField
                  label="제목 (이미지 안)"
                  helperText="선택 입력"
                  inputProps={{ maxLength: 160 }}
                  {...field('title')}
                />
                <TextField
                  label="설명 (이미지 안)"
                  helperText="선택 입력"
                  inputProps={{ maxLength: 240 }}
                  {...field('description')}
                />
                <TextField
                  label="주황색 버튼 문구"
                  required
                  inputProps={{ maxLength: 120 }}
                  placeholder="음감회 이벤트 응모하기"
                  {...field('actionLabel')}
                />
                <TextField
                  label="외부 응모 URL"
                  type="url"
                  required
                  inputProps={{ maxLength: 2048 }}
                  {...field('destinationUrl')}
                />
                <TextField
                  label="영문 제목 (선택)"
                  inputProps={{ maxLength: 160 }}
                  {...field('titleEn')}
                />
                <TextField
                  label="영문 설명 (선택)"
                  inputProps={{ maxLength: 240 }}
                  {...field('descriptionEn')}
                />
                <TextField
                  label="영문 버튼 문구 (선택)"
                  inputProps={{ maxLength: 120 }}
                  {...field('actionLabelEn')}
                />
                {['isPinned', 'showAdMark', 'isActive'].map((name, index) => (
                  <FormControlLabel
                    key={name}
                    label={['상단 고정', 'AD 마크 표시', '앱·웹에 공개'][index]}
                    control={
                      <Checkbox
                        checked={form[name]}
                        onChange={(event) => {
                          if (!pending.current) setForm({ ...form, [name]: event.target.checked });
                        }}
                      />
                    }
                  />
                ))}
                <Typography variant="caption">
                  미리보기 (실제 앱 글꼴과 줄바꿈은 기기에 따라 다를 수 있습니다)
                </Typography>
                <EventCreative item={{ ...form, imageUrl: preview || form.imageUrl }} preview />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button disabled={busy} onClick={closeForm}>
                취소
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? '저장 중…' : '저장'}
              </Button>
            </DialogActions>
          </Box>
        )}
      </Dialog>
    </Stack>
  );
}

export default function EventAdManagement() {
  const user = useUserStore((state) => state.user);
  return <EventSession key={JSON.stringify([user?.id, user?.role])} />;
}
